import React, { useState, useEffect, useCallback } from "react";
import { Button, Select, Switch, Tag, Popconfirm } from "antd";
import { DeleteOutlined, UserOutlined, PlusOutlined } from "@ant-design/icons";
import {
  getUser,
  getUsers,
  updateUserRole,
  updateUserEnabled,
  deleteUser,
} from "../network/api";
import AddUser from "./Modals/addUser";
import PageHeader from "./common/PageHeader";
import DataTable from "./common/DataTable";
import { notify, serverError } from "../utils/notify";
import {
  ROLE_OPTIONS,
  ROLE_TAG_COLOR,
  ROLE_LABEL,
  rankOf,
  canDeleteUsers,
} from "../constants/roles";

const Users = () => {
  const [currentUser, setCurrentUser] = useState(null);
  const [users, setUsers] = useState([]);
  const [visible, setVisible] = useState(false);

  // Server-side filters wired to the query params (search is handled client-side
  // by DataTable since the list is small/already loaded).
  const [roleFilter, setRoleFilter] = useState();
  const [enabledFilter, setEnabledFilter] = useState();

  const fetchCurrentUser = useCallback(async () => {
    try {
      const user = await getUser();
      setCurrentUser(user);
    } catch (error) {
      console.error("Error fetching current user:", error);
    }
  }, []);

  const fetchUsers = useCallback(async () => {
    try {
      const response = await getUsers({
        role: roleFilter,
        enabled: enabledFilter,
      });
      const formattedData = (response.users || []).map((item) => ({
        key: item._id,
        _id: item._id,
        name: item.name,
        email: item.email,
        role: item.role,
        enabled: item.enabled,
        createdAt: item.createdAt
          ? new Date(item.createdAt).toLocaleString()
          : "",
      }));
      setUsers(formattedData);
    } catch (error) {
      console.error("Error fetching users:", error);
      notify.error(serverError(error, "Failed to load users."));
    }
  }, [roleFilter, enabledFilter]);

  useEffect(() => {
    fetchCurrentUser();
  }, [fetchCurrentUser]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const currentRole = currentUser?.role;
  const currentUserId = currentUser?._id;

  // Mirror backend rules so the UI never offers forbidden actions.
  // You may modify a target only if it ranks strictly below you and is not yourself.
  const canModifyTarget = (record) =>
    currentRole &&
    record._id !== currentUserId &&
    rankOf(record.role) < rankOf(currentRole);

  // You can only assign roles <= your own rank.
  const assignableRoleOptions = ROLE_OPTIONS.filter(
    (opt) => rankOf(opt.value) <= rankOf(currentRole)
  );

  const handleRoleChange = async (record, role) => {
    try {
      await updateUserRole(record._id, role);
      notify.success("Role updated successfully!");
      fetchUsers();
    } catch (error) {
      console.error("Error updating role:", error);
      notify.error(serverError(error, "Failed to update role."));
    }
  };

  const handleEnabledChange = async (record, enabled) => {
    try {
      await updateUserEnabled(record._id, enabled);
      notify.success(`User ${enabled ? "enabled" : "disabled"} successfully!`);
      fetchUsers();
    } catch (error) {
      console.error("Error updating status:", error);
      notify.error(serverError(error, "Failed to update status."));
    }
  };

  const handleDelete = async (record) => {
    try {
      await deleteUser(record._id);
      notify.success("User deleted successfully!");
      fetchUsers();
    } catch (error) {
      console.error("Error deleting user:", error);
      notify.error(serverError(error, "Failed to delete user."));
    }
  };

  const onSubmit = () => {
    fetchUsers();
  };

  const columns = [
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
      sorter: (a, b) => (a.name || "").localeCompare(b.name || ""),
    },
    {
      title: "Email",
      dataIndex: "email",
      key: "email",
      sorter: (a, b) => (a.email || "").localeCompare(b.email || ""),
    },
    {
      title: "Role",
      dataIndex: "role",
      key: "role",
      render: (role, record) => {
        // Inline select to change role when allowed, otherwise a read-only tag.
        if (canModifyTarget(record)) {
          return (
            <Select
              value={record.role}
              style={{ width: 160 }}
              options={assignableRoleOptions}
              onChange={(value) => handleRoleChange(record, value)}
            />
          );
        }
        return (
          <Tag color={ROLE_TAG_COLOR[role]}>{ROLE_LABEL[role] || role}</Tag>
        );
      },
    },
    {
      title: "Status",
      dataIndex: "enabled",
      key: "enabled",
      render: (enabled, record) => (
        <Switch
          checked={enabled}
          disabled={!canModifyTarget(record)}
          checkedChildren="Enabled"
          unCheckedChildren="Disabled"
          onChange={(checked) => handleEnabledChange(record, checked)}
        />
      ),
    },
    {
      title: "Created",
      dataIndex: "createdAt",
      key: "createdAt",
      sorter: (a, b) => new Date(a.createdAt) - new Date(b.createdAt),
    },
    {
      title: "Actions",
      key: "actions",
      render: (_, record) => {
        // Delete is superAdmin only, and never on your own row.
        if (!canDeleteUsers(currentRole) || record._id === currentUserId) {
          return null;
        }
        return (
          <Popconfirm
            title="Delete this user?"
            description="This action cannot be undone."
            okText="Delete"
            okButtonProps={{ danger: true }}
            cancelText="Cancel"
            onConfirm={() => handleDelete(record)}
          >
            <Button danger icon={<DeleteOutlined />} size="small">
              Delete
            </Button>
          </Popconfirm>
        );
      },
    },
  ];

  const toolbar = (
    <>
      <Select
        placeholder="Filter by role"
        allowClear
        style={{ width: 180 }}
        value={roleFilter}
        options={ROLE_OPTIONS}
        onChange={(value) => setRoleFilter(value)}
      />
      <Select
        placeholder="Filter by status"
        allowClear
        style={{ width: 160 }}
        value={enabledFilter}
        options={[
          { value: "true", label: "Enabled" },
          { value: "false", label: "Disabled" },
        ]}
        onChange={(value) => setEnabledFilter(value)}
      />
    </>
  );

  return (
    <div>
      <PageHeader
        title="User Management"
        subtitle="Manage operators, roles, and access"
        icon={<UserOutlined />}
        extra={
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => setVisible(true)}
          >
            Create User
          </Button>
        }
      />

      <DataTable
        rowKey="_id"
        columns={columns}
        dataSource={users}
        searchableKeys={["name", "email"]}
        searchPlaceholder="Search by name or email"
        toolbar={toolbar}
        scrollX={800}
      />

      <AddUser
        visible={visible}
        currentUserRole={currentRole}
        onClose={() => setVisible(false)}
        onSubmit={onSubmit}
      />
    </div>
  );
};

export default Users;
